import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { USER_ROLES } from '@messe/shared'
import type { PublicUser, UserRole } from '@messe/shared'
import * as api from './api.js'

interface AuthState {
  user: PublicUser | null
  /** True until the initial silent refresh has settled. */
  isLoading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  hasRole: (...roles: UserRole[]) => boolean
}

const AuthContext = createContext<AuthState | null>(null)

/**
 * Holds the signed-in user for the app.
 *
 * On mount it attempts a silent refresh. The access token never survives a
 * reload by design, so without this every refresh of the page would look like a
 * logout even though the session is perfectly alive in the httpOnly cookie.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // AbortController rather than a boolean flag: TypeScript's control-flow
    // analysis cannot see that a closure reassigns the flag later, so a plain
    // `let cancelled` reads as a constant to both the compiler and the linter.
    const controller = new AbortController()
    // Read through a function: an inline `controller.signal.aborted` gets
    // narrowed to `false` by the first check and every later one looks constant
    // to the compiler, even though abort() can flip it at any point.
    const isAborted = () => controller.signal.aborted

    void (async () => {
      const restored = await api.refreshSession()
      if (isAborted()) return

      if (restored) {
        try {
          const me = await api.fetchMe()
          if (!isAborted()) setUser(me)
        } catch {
          if (!isAborted()) setUser(null)
        }
      }

      if (!isAborted()) setIsLoading(false)
    })()

    return () => {
      controller.abort()
    }
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    const tokens = await api.login(email, password)
    setUser(tokens.user)
  }, [])

  const signOut = useCallback(async () => {
    await api.logout()
    setUser(null)
  }, [])

  /**
   * Whether the signed-in account holds at least one of these roles.
   *
   * By rank, not equality: USER_ROLES runs most to least privileged, so a
   * platform admin satisfies a check written for `admin`. Equality would have
   * hidden every navigation entry from the one role that can see everything.
   */
  const hasRole = useCallback(
    (...roles: UserRole[]) => {
      if (user === null) return false
      const rank = (role: UserRole) => USER_ROLES.indexOf(role)
      return rank(user.role) <= Math.min(...roles.map(rank))
    },
    [user],
  )

  const value = useMemo<AuthState>(
    () => ({ user, isLoading, signIn, signOut, hasRole }),
    [user, isLoading, signIn, signOut, hasRole],
  )

  return <AuthContext value={value}>{children}</AuthContext>
}

export function useAuth(): AuthState {
  const context = use(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used inside an AuthProvider')
  }
  return context
}
